import { useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { formatMutationResource } from 'utils/misc';
import { mapSlotGeomAtoms } from 'pages/type-curves/inventory-surface/atoms';
import {
  builderScopedUnitAtom,
  builderSetbackAtom,
  ltdLateralOverridesAtom,
} from './atoms';
import { extendLateralToBoundary } from './ltd-lateral-trim';
import { useUnitBoundaryGeometry } from './use-unit-boundary-geometry';

/** One resolved LTD lateral: its full API line, current (trimmed) line, `valued`. */
export interface LtdLateral {
  slotId: number;
  /** The full (un-trimmed) API lateral, or null when not loaded. */
  fullCoords: GeoJSON.Position[] | null;
  /**
   * The furthest the lateral may be edited out to (unit boundary less the
   * in-line setback); `fullCoords` itself when that isn't known.
   */
  maxCoords: GeoJSON.Position[] | null;
  /** The current lateral — the overlay's trim if any, else the full API line. */
  currentCoords: GeoJSON.Position[] | null;
  /** The slot's `valued` flag; null when the slot isn't in the map results. */
  valued: boolean | null;
}

interface LtdLaterals {
  /** One entry per requested slot id, in that order. */
  laterals: LtdLateral[];
  /** Write several slots' trimmed coords into the shared overlay in one update. */
  setManyCoords: (entries: Array<[number, GeoJSON.Position[]]>) => void;
}

/**
 * The map laterals for a set of LTD slots, resolved from the builder's map-slots
 * query and the trim overlay — the common source of truth for both the map grips
 * and the rail's Lateral-length field. Reads the builder's OWN
 * `mapSlotGeomAtoms.builder` (the copy the builder map already fetches, separate
 * from the main-page map), so it never triggers its own request.
 *
 * @param {number[]} slotIds - Numeric slot ids to resolve.
 * @return {LtdLaterals} The resolved laterals and a bulk writer.
 */
export const useLtdLaterals = (slotIds: readonly number[]): LtdLaterals => {
  const overrides = useAtomValue(ltdLateralOverridesAtom);
  const setOverrides = useSetAtom(ltdLateralOverridesAtom);
  const slotsQuery = useAtomValue(mapSlotGeomAtoms.builder);
  const { data } = formatMutationResource(slotsQuery);
  // Same query key as the modal's boundary read, so this is a cache hit.
  const { unitId } = useAtomValue(builderScopedUnitAtom);
  const { ring } = useUnitBoundaryGeometry(unitId);
  const { inLineSetbackFt } = useAtomValue(builderSetbackAtom);

  // The server lateral geometry is used as-is: it already sits inside the unit's
  // true footprint, so the trim editor, its drag maths, and the gun-barrel
  // length/trim readouts all work in raw server space. (The earlier client
  // re-centre — which slid the group to fit the frame-derived rectangle boundary —
  // is gone; see `tc-slot-geometries-layer.tsx`.)
  const key = slotIds.join(',');
  const laterals = useMemo<LtdLateral[]>(() => {
    return slotIds.map((slotId) => {
      const r = data?.results?.find((x) => x.id === slotId);
      const fullCoords =
        r?.geometryGeojson?.type === 'LineString'
          ? r.geometryGeojson.coordinates
          : null;
      const currentCoords = overrides.get(slotId) ?? fullCoords;
      const maxCoords = fullCoords
        ? extendLateralToBoundary(fullCoords, ring, inLineSetbackFt)
        : null;
      return {
        slotId,
        fullCoords,
        maxCoords,
        currentCoords,
        valued: r?.valued ?? null,
      };
    });
    // `key` stands in for `slotIds` (a fresh array each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, data, overrides, ring, inLineSetbackFt]);

  const setManyCoords = useCallback(
    (entries: Array<[number, GeoJSON.Position[]]>) => {
      if (entries.length === 0) return;
      setOverrides((prev) => {
        const next = new Map(prev);
        for (const [id, coords] of entries) next.set(id, coords);
        return next;
      });
    },
    [setOverrides]
  );

  return { laterals, setManyCoords };
};
